import { Fragment, useState, type FormEvent } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock } from 'react-feather';
import { appToast, toastApiError } from '@/core/error/toast';
import { resetPassword } from '@/features/auth/api';
import logo from '@/assets/images/logo/logo.png';
import authIllustration from '@/assets/images/login/auth-img.svg';

const FEATURES = [
  'Secure one-time reset link from your administrator',
  'Choose a strong password of at least 8 characters',
  'Sign in immediately after updating your password',
];

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token')?.trim() ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [togglePassword, setTogglePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) {
      appToast.error('This reset link is missing or invalid. Request a new one.');
      return;
    }
    if (password !== confirm) {
      appToast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await resetPassword(token, password);
      appToast.success(res.message);
      navigate('/login', { replace: true });
    } catch (err) {
      toastApiError(err, 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <div className="credora-login">
        <div className="credora-login__shell">
          <aside className="credora-login__brand" aria-hidden="true">
            <div className="credora-login__brand-inner">
              <p className="credora-login__eyebrow">Account security</p>
              <h1 className="credora-login__headline">
                Choose a
                <span> new password</span>
              </h1>
              <p className="credora-login__tagline">
                Use the secure link from your email to choose a new password for your account.
              </p>
              <ul className="credora-login__features">
                {FEATURES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="credora-login__illustration">
                <img src={authIllustration} alt="" />
              </div>
            </div>
          </aside>

          <main className="credora-login__panel">
            <div className="credora-login__card">
              <Link className="credora-login__logo credora-brand-logo" to="/login">
                <img src={logo} alt="RCATS" className="img-fluid credora-brand-logo__img" />
              </Link>
              <div className="credora-login__form-header">
                <h4>Reset password</h4>
                <p>
                  {token
                    ? 'Enter and confirm your new password.'
                    : 'Open the reset link from your email to continue.'}
                </p>
              </div>
              {!token ? (
                <p className="text-muted">
                  Need a new link? <Link to="/forgot-password">Request a password reset</Link>.
                </p>
              ) : null}
              <Form className="theme-form credora-login__form" onSubmit={onSubmit}>
                <FormGroup>
                  <Label className="col-form-label" for="new-password">
                    New password
                  </Label>
                  <div className="credora-login__input-wrap">
                    <Lock className="credora-login__input-icon" size={18} aria-hidden="true" />
                    <Input
                      id="new-password"
                      type={togglePassword ? 'text' : 'password'}
                      required
                      minLength={8}
                      className="form-control credora-login__input credora-login__input--password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="credora-login__toggle-password"
                      onClick={() => setTogglePassword(!togglePassword)}
                      aria-label={togglePassword ? 'Hide password' : 'Show password'}
                    >
                      {togglePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label" for="confirm-password">
                    Confirm password
                  </Label>
                  <div className="credora-login__input-wrap">
                    <Lock className="credora-login__input-icon" size={18} aria-hidden="true" />
                    <Input
                      id="confirm-password"
                      type="password"
                      required
                      minLength={8}
                      className="form-control credora-login__input"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </div>
                </FormGroup>
                <button
                  type="submit"
                  className="btn btn-primary w-100 credora-login__submit"
                  disabled={!token || submitting}
                >
                  {submitting ? 'Updating…' : 'Update password'}
                </button>
              </Form>
              <p className="text-center mt-3 mb-0">
                <Link to="/login">Back to sign in</Link>
              </p>
              <p className="credora-login__footer-note">
                Authorized staff and parents only.
              </p>
            </div>
          </main>
        </div>
      </div>
    </Fragment>
  );
}
